import { create } from "zustand";
import {
  api,
  CreateKnowledgeInput,
  KnowledgeCategory,
  KnowledgeFilter,
  KnowledgeItem,
  UpdateKnowledgePatch,
} from "../lib/tauri";

interface KnowledgeState {
  items: KnowledgeItem[];
  loaded: boolean;
  /** 検索ボックスの文字列 */
  search: string;
  /** カテゴリ絞り込み（null = 全部） */
  category: KnowledgeCategory | null;
  /** タグ絞り込み（null = 全部） */
  tag: string | null;
  load: () => Promise<void>;
  setSearch: (search: string) => void;
  setCategory: (category: KnowledgeCategory | null) => void;
  setTag: (tag: string | null) => void;
  create: (input: CreateKnowledgeInput) => Promise<KnowledgeItem | undefined>;
  update: (id: string, patch: UpdateKnowledgePatch) => Promise<void>;
  remove: (id: string) => Promise<void>;
  rate: (id: string, usefulness: number) => Promise<void>;
  touchUsed: (id: string) => Promise<void>;
}

function currentFilter(state: KnowledgeState): KnowledgeFilter {
  const filter: KnowledgeFilter = {};
  const s = state.search.trim();
  if (s) filter.search = s;
  if (state.category) filter.category = state.category;
  if (state.tag) filter.tag = state.tag;
  return filter;
}

export const useKnowledgeStore = create<KnowledgeState>((set, get) => ({
  items: [],
  loaded: false,
  search: "",
  category: null,
  tag: null,
  load: async () => {
    try {
      const items = (await api.listKnowledge?.(currentFilter(get()))) ?? [];
      set({ items, loaded: true });
    } catch {
      // PWA など未対応環境: 空で動かす（projectStore / goalStore と同型）
      set({ items: [], loaded: true });
    }
  },
  setSearch: (search) => {
    set({ search });
    void get().load();
  },
  setCategory: (category) => {
    set({ category });
    void get().load();
  },
  setTag: (tag) => {
    set({ tag });
    void get().load();
  },
  create: async (input) => {
    const created = await api.addKnowledge?.(input);
    await get().load();
    return created;
  },
  update: async (id, patch) => {
    await api.updateKnowledge?.(id, patch);
    await get().load();
  },
  remove: async (id) => {
    await api.deleteKnowledge?.(id);
    await get().load();
  },
  rate: async (id, usefulness) => {
    await api.rateKnowledge?.(id, usefulness);
    await get().load();
  },
  touchUsed: async (id) => {
    await api.touchKnowledgeUsed?.(id);
    await get().load();
  },
}));
